import { useRef, useCallback } from "react";
import { toast } from "react-toastify";

/**
 * Hook to prevent duplicate error messages
 * Uses a ref to track if an error has already been shown
 */
export const useErrorHandler = () => {
  const errorShownRef = useRef(false);
  const lastErrorRef = useRef(null);

  const showError = useCallback((error, customMessage = null) => {
    const errorKey = error?.response?.status || error?.message || "unknown";

    // Reset if it's a different error
    if (lastErrorRef.current !== errorKey) {
      errorShownRef.current = false;
      lastErrorRef.current = errorKey;
    }

    // Prevent duplicate errors
    if (errorShownRef.current) {
      return;
    }

    errorShownRef.current = true;

    const errorMessage =
      customMessage ||
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      error?.message ||
      "An error occurred";

    toast.error(errorMessage);

    // Reset after a delay to allow retries
    setTimeout(() => {
      errorShownRef.current = false;
    }, 3000);
  }, []);

  const reset = useCallback(() => {
    errorShownRef.current = false;
    lastErrorRef.current = null;
  }, []);

  return { showError, reset };
};
